import { Alert, AlertContext } from "@minervaproject/fds";
import jwt from "jsonwebtoken";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { BugReportTable } from "./bug_report_table";
import { ClassInfo } from "./class_info";
import { ClassStats } from "./class_stats";
import { IntercomConversationTable } from "./intercom_conversation_table";
import { MonitoringEventTable } from "./monitoring_event_table";
import { PreclassCheckFailureTable } from "./preclass_check_failures";
import { RTTBarChart } from "./rtt_chart";
import { Section } from "./section";
import { SupportActionTable } from "./support_action_table";
import { TimeFilter } from "./time_filter";
import { UserStats } from "./user_stats";

import { LocalModal } from "components/local_modal";
import { MonitoringPubsubMessageObserver } from "components/observers/monitoring_pubsub_message_observer";
import { ForumAPIContext } from "contexts/forum_api_context";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { PageUnloadReasonType, PubsubErrorCodes } from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { setIntercomConversations } from "store/reducers/local_monitoring";
import { selectLoading } from "store/selectors/classroom";
import { selectPubsubErrorCode } from "store/selectors/local";
import { selectIntercomConversations } from "store/selectors/local_monitoring";
import {
  selectMonitoringEventsExistByType,
  selectSupportActionsExist,
} from "store/selectors/monitoring";
import { redirectToClassEditPage, redirectToHome, redirectToLogin } from "utils/classroom";
import { getAuthToken } from "utils/cookie";

function MonitoringClassDashboard() {
  const dispatch = useAppDispatch();
  const { alerts, ref } = useContext(AlertContext);
  const { classId } = useParams();
  const forumAPI = useContext(ForumAPIContext);
  const isLoading = useAppSelector(selectLoading());
  const pubsubErrorCode = useAppSelector(selectPubsubErrorCode());
  const bugReportsExist = useAppSelector(
    selectMonitoringEventsExistByType(MonitoringEventTypeType.BUG_REPORT_SUBMITTED),
  );
  const preclassCheckFailsExist = useAppSelector(
    selectMonitoringEventsExistByType(MonitoringEventTypeType.PRECLASS_CHECK_FAIL),
  );
  const pubsubStatsExist = useAppSelector(
    selectMonitoringEventsExistByType(MonitoringEventTypeType.PUBSUB_STATS),
  );
  const supportActionsExist = useAppSelector(selectSupportActionsExist());
  const [canAccessMonitoringDashboard, setCanAccessMonitoringDashboard] = useState(false);
  const intercomConversations = useArraySelector(selectIntercomConversations());

  const flightAuthToken = getAuthToken();

  useEffect(() => {
    if (!flightAuthToken) {
      console.error("[MonitoringClassDashboard] Auth token not found");
      redirectToLogin();
      return;
    }

    const flightAuth = jwt.decode(flightAuthToken);
    if (!flightAuth || typeof flightAuth !== "object" || !flightAuth.capabilities) {
      console.error("[MonitoringClassDashboard] Failed to decode JWT");
      redirectToLogin();
      return;
    }

    if (flightAuth.capabilities["can access monitoring dashboard"]) {
      setCanAccessMonitoringDashboard(true);
    } else {
      console.error("[MonitoringClassDashboard] User does not have access to monitoring dashboard");
      redirectToHome(PageUnloadReasonType.NOT_PERMITTED_TO_MONITOR_CLASS);
    }
  }, [flightAuthToken]);

  useEffect(() => {
    if (isLoading || !classId) return;
    const fetchIntercomConversations = async () => {
      const conversations = await forumAPI.getIntercomConversations(classId);
      dispatch(setIntercomConversations(conversations));
    };
    fetchIntercomConversations();
  }, [classId, dispatch, forumAPI, isLoading]);

  useEffect(() => {
    if (!classId) return;
    if (pubsubErrorCode === PubsubErrorCodes.CLASS_NOT_IN_FLIGHT) {
      redirectToClassEditPage({ classId }, PageUnloadReasonType.CLASS_NOT_IN_FLIGHT_FOR_MONITORING);
    } else if (pubsubErrorCode === PubsubErrorCodes.CLASS_EXPIRED) {
      redirectToClassEditPage({ classId }, PageUnloadReasonType.CLASS_EXPIRED);
    }
  }, [classId, pubsubErrorCode]);

  if (!canAccessMonitoringDashboard) return null;

  return (
    <div className="vertical-scroll" style={{ height: "100vh" }}>
      <div className="position-relative bg-blue">
        <div className="h1 py4 mx-auto bg-blue text-white mb0 center">{`Flight Monitoring Dashboard - Class ${classId}`}</div>
      </div>
      <div className="m5">
        {alerts.map((alert) => (
          <Alert ref={ref} id={alert.id} key={alert.id} kind={alert.kind}>
            {alert.content}
          </Alert>
        ))}
      </div>
      <TimeFilter />
      <div className="px6">
        <Section title="Class Info" isAlwaysOpen>
          <ClassInfo />
        </Section>
        <Section title="Class Stats" isAlwaysOpen>
          <ClassStats intercomConversationCount={intercomConversations.length} />
        </Section>
        <Section title="Support Actions" isInitiallyOpen disabled={!supportActionsExist}>
          <SupportActionTable />
        </Section>{" "}
        <Section title="Bug Reports" isInitiallyOpen disabled={!bugReportsExist}>
          <BugReportTable />
        </Section>
        <Section title="Stats Per User" isInitiallyOpen>
          <UserStats />
        </Section>
        <Section title="Pubsub" disabled={!pubsubStatsExist}>
          <RTTBarChart />
        </Section>
        <Section title="Pre-class Check Failures" disabled={!preclassCheckFailsExist}>
          <PreclassCheckFailureTable />
        </Section>
        <Section title="Intercom Conversations" disabled={intercomConversations.length === 0}>
          <IntercomConversationTable intercomConversations={intercomConversations} />
        </Section>
        <Section title="Monitoring Events" isInitiallyOpen id="monitoring-events-section">
          <MonitoringEventTable />
        </Section>
      </div>
      <LocalModal />
      <MonitoringPubsubMessageObserver />
    </div>
  );
}

export { MonitoringClassDashboard };
